import { createAsyncThunk } from '@reduxjs/toolkit';
import { WellDetailsStates } from 'business/models/CustomModels';
import { DrawingLayer } from 'business/models/DrawingLayer';
import {
  ExperimentInfoImagingScheduleDTO,
  InspectionSetDTO,
  LayerDTO,
  LayerDetailsDTO,
  PlateIngredientAndDrawingRequestDTO,
  SaveExprimentInfoDTO,
} from 'business/models/Models';
import ExperimentService from 'business/services/ExperimentService';
import MarcoService from 'business/services/MarcoService';
import PlateService from 'business/services/PlateService';
import RecentDocumentService from 'business/services/RecentDocumentService';
import ScreenService from 'business/services/ScreenService';
import { TreeService } from 'business/services/TreeService';
import {
  createPlateLables,
  getDefaultCaptureProfileId,
  getDefaultInspection,
  getDefaultRegion,
  getDefaultWellSelection,
  getWellAndCanvasLabels,
} from 'components/commandpanel/Navigator/DRCIPWUtil';
import {
  Plate,
  PlateCompositeImage,
  PlateDrawingIdentifiers,
  PlateImageStructure,
  DropImageParameters,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { getExperimentPlateSize } from 'components/main/Experiment';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { cloneDeep } from 'lodash';
import { Dispatch } from 'redux';
import { AppDispatch } from 'redux/configureStore';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  appendInspection,
  prepareAndSetExperimentDataAfterSave,
  removeInspection,
  preserveExpData,
  setPlateSize,
  updateExperimentDataState,
  upsertImagingSchedule,
  getCompositeImageStoreImageByKey,
  setPlateImagesStructure,
  setExperiment,
  updateSelectedWellInspection,
  setAllowLayerEditing,
  setWellSelection,
  setIsFetchingExperimentData,
  setAutoScoreIgnoredStatus as setExperimentAutoScoreIgnoredStatus,
} from 'redux/reducers/experimentDataReducer';
import { setAutoScoreIgnoredStatus as setSearchAutoScoreIgnoredStatus } from 'redux/reducers/searchnFilterReducer';
import { setIsEditMode, setLayersInfo } from 'redux/reducers/experimentReducer';
import { getCompositeImageKeysAsString } from 'redux/utils/experiment';
import {
  getPlainExperimentSaveInfoResponse,
  getPlainPlateScores,
} from 'transformers/experiment';
import { getPlainObject } from 'transformers/utils';
import {
  generateEmptyCanvasItems,
  getPlatesImagesStructureData,
  ProductEditionAllowedLayersMap,
} from 'utils/ExperimentUtils';
import {
  DisplayModes,
  isExpInfoPath,
  SetCanvasLabels,
  SetDisplayMode,
  SetExpInspections,
  SetImagingStatus,
  SetWellsLabels,
  showAlertSnackBar,
} from 'utils/Global';
import useRockFetchGet from 'utils/RockFetch';
import { fetchWellDropImageMetaDataThunk } from './well';
import { buildRegionIdLookupCache } from 'components/well/utils';
import { EProductEditions } from 'enums/EProductEditions';
import { EPlateState } from 'enums/EPlatestate';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { getIgnoreAutoScoreErrorMessage } from 'constants/messages/ignoreAutoScore';
const expService = new ExperimentService();
const treeService = new TreeService();
const screenService = new ScreenService();
const recentDocumentService = new RecentDocumentService();
const plateService = new PlateService();
const marcoService = new MarcoService();

type SetAutoScoreIgnoredParams = {
  plateId: number;
  imageBatchId: number;
  wellNumber: number;
  dropNumber: number;
  isAutoScoreIgnored: boolean;
  dropId?: number;
};

export const setAutoScoreIgnoredStatusThunk =
  (params: SetAutoScoreIgnoredParams) => async (dispatch: AppDispatch) => {
    const { plateId, imageBatchId, wellNumber, dropNumber, isAutoScoreIgnored, dropId } =
      params;
    const rollbackPayload = {
      plateId,
      imageBatchId,
      wellNumber,
      dropNumber,
      isAutoScoreIgnored: !isAutoScoreIgnored,
    };

    dispatch(setExperimentAutoScoreIgnoredStatus(params));
    if (dropId !== undefined && dropId !== null) {
      dispatch(
        setSearchAutoScoreIgnoredStatus({
          dropId,
          imageBatchId,
          isAutoScoreIgnored,
        }),
      );
    }

    try {
      const ignoreAutoScoreResponse = await marcoService.IgnoreAutoScore(
        plateId,
        imageBatchId,
        wellNumber,
        dropNumber,
        isAutoScoreIgnored,
      );

      if (ignoreAutoScoreResponse) {
        return true;
      }
    } catch (error) {
      console.error('Failed to update ignored auto score status:', error);
    }

    dispatch(setExperimentAutoScoreIgnoredStatus(rollbackPayload));
    if (dropId !== undefined && dropId !== null) {
      dispatch(
        setSearchAutoScoreIgnoredStatus({
          dropId,
          imageBatchId,
          isAutoScoreIgnored: !isAutoScoreIgnored,
        }),
      );
    }

    showAlertSnackBar(
      EAlertType.ERROR,
      ESnackBarPosition.TOP,
      getIgnoreAutoScoreErrorMessage(isAutoScoreIgnored),
    );

    return false;
  };

export const fetchLayers = async (
  expid: number,
  rockFetchGet,
): Promise<DrawingLayer[]> => {
  const [tempLyrs, tempLyrDetails] = await Promise.all([
    expService.getLayers(expid),
    rockFetchGet(`/api/Experiment/layerdetails/${expid}`, 'Get', undefined, true),
  ]);
  const layers: DrawingLayer[] = tempLyrs
    ? tempLyrs.map((tl) => LayerDTO.fromJS(tl))
    : new Array<DrawingLayer>();
  if (!layers) {
    return null;
  }

  const layersDetails: LayerDetailsDTO[] = tempLyrDetails
    ? tempLyrDetails.map((tld) => LayerDetailsDTO.fromJS(tld))
    : new Array<LayerDetailsDTO>();
  if (layersDetails && layers) {
    layersDetails.forEach((ld) => {
      const tempLyr = layers.find((l) => l.id == ld.layerId);

      if (tempLyr) {
        tempLyr.layerDetail = ld;
      }
    });
  }
  return layers;
};

export const useProcessAndSetExperimentAfterSaveHandler = () => {
  const expId = useRMSelector((state) => state.experiment.expData?.experimentId);
  const [rockFetchGet] = useRockFetchGet();
  const dispatch = useRMDispatch();
  return async (saveResult: SaveExprimentInfoDTO) => {
    const [layers, experimentInfo, imagingSchedule] = await Promise.all([
      fetchLayers(expId, rockFetchGet),
      expService.getExperimentInfo(expId),
      await expService.getImagingSchedule(expId, true),
    ]);
    dispatch(
      prepareAndSetExperimentDataAfterSave({
        saveResult: getPlainExperimentSaveInfoResponse(saveResult),
        layers: layers,
        expInfo: experimentInfo,
        imagingSchedule: imagingSchedule,
      }),
    );
  };
};

export const deleteInspectionsAsync =
  (selectedInspection: InspectionSetDTO) => async (dispatch: Dispatch, getState) => {
    await treeService.deleteInspectionSet(selectedInspection.groupedInspections);
    dispatch(removeInspection(selectedInspection.id));
    const allInspections = getState()?.experiment?.expData?.inspections;
    const selectedInspectionSet: InspectionSetDTO[] = [];
    if (allInspections?.length > 0) {
      selectedInspectionSet.push(allInspections[allInspections.length - 1]);
    } else if (allInspections?.length === 0) {
      const newInspectionSet: InspectionSetDTO = {
        id: 0,
        name: '+00:00',
        groupedInspections: [],
        init: function (_data?: any): void {
          throw new Error('Function not implemented.');
        },
        toJSON: function (data?: any) {
          throw new Error('Function not implemented.');
        },
      };
      selectedInspectionSet.push(newInspectionSet);
      dispatch(appendInspection(newInspectionSet));
    }
    const index = selectedInspectionSet.findIndex((ins) => ins.groupedInspections.length);
    if (index >= 0) {
      dispatch(updateSelectedWellInspection(selectedInspectionSet[index]));
    } else {
      SetDisplayMode(DisplayModes.DESIGN);
    }
  };

export const fetchImagingSchedulesAsync = async (expId: number, dispatch: Dispatch) => {
  const imgSchedules: ExperimentInfoImagingScheduleDTO =
    await expService.getImagingSchedule(expId, true);
  if (imgSchedules === null) {
    return null;
  }
  if (dispatch) {
    dispatch(upsertImagingSchedule({ imagingScheduleDetails: imgSchedules }));
  }
  return imgSchedules;
};

export const fetchExperimentAsync = (
  expId: number,
  documentType: ETreeNodeTypes.Experiment | ETreeNodeTypes.Screen,
  productEdition?: EProductEditions,
  wellNumber?: number,
  plateId?: number,
  setExperimentName?: (name: string) => void,
  setBreadCrumbs?: (data: any) => void,
) => {
  const [rockFetchGet] = useRockFetchGet();
  return async (dispatch: AppDispatch, getState) => {
    dispatch(setIsFetchingExperimentData(true));
    treeService
      .getDocumentParentNodes(expId, ETreeNodeTypes.Experiment, true)
      .then((data) => {
        setExperimentName(data?.[0]?.name ?? expData.expName);
        setBreadCrumbs(data);
      })
      .catch((error) => {
        console.error('Failed to fetch breadcrumbs:', error);
        showAlertSnackBar(
          EAlertType.ERROR,
          ESnackBarPosition.TOP,
          'Failed to fetch breadcrumbs.',
        );
        setExperimentName(expData.expName);
        setBreadCrumbs([]);
      });
    const allPlateScoresPromise =
      expService.getAllScoresOfExperimentPlateWiseByExpId(expId);
    const layersPromise = fetchLayers(expId, rockFetchGet);
    const expData =
      documentType === ETreeNodeTypes.Experiment
        ? await expService.getExperimentService(expId, true)
        : await screenService.getScreenById(expId);
    if (!expData) {
      dispatch(setIsFetchingExperimentData(false));
      return;
    }
    const plateImageStructure = getPlatesImagesStructureData(expData);
    buildRegionIdLookupCache(expData.regions);
    dispatch(setPlateImagesStructure(plateImageStructure));
    const inspectionSet = getDefaultInspection(expData);
    const { wellNavigationDetails, dropImageParameters } =
      getDefaultWellSelection(expData);
    if (inspectionSet) {
      dispatch(
        fetchAllCompositeImagesThunk({
          plates: plateImageStructure,
          wellNavigationDetails: wellNavigationDetails[0],
          wellImagingSettingDetails: dropImageParameters[0],
          regenerate: false,
        }),
      );
      dispatch(
        fetchWellDropImageMetaDataThunk({
          experimentId: expId,
          inspectionId: inspectionSet.id,
        }),
      );
    }
    const tempExp = cloneDeep(expData);
    dispatch(setExperiment(tempExp));
    dispatch(setIsEditMode(!expData.isDispensed));
    SetExpInspections(expData.inspections);
    SetImagingStatus(expData.isImageModeActive);
    const { wellLabels, canvasLabels } = getWellAndCanvasLabels(
      expData.containerCols,
      expData.containerRows,
    );
    SetWellsLabels(wellLabels);
    SetCanvasLabels(canvasLabels);
    expData.wellLables = wellLabels;
    const layers = await layersPromise;
    expData.layers = layers;

    const loadIngredientsPromise = expService.loadAllPlatesIngredients(
      expData.plateDto,
      expData.containerMaxDropVolume,
      expData.layers,
      getDefaultRegion(expData),
      getDefaultCaptureProfileId(expData),
    );
    if (
      layers?.some(
        (l) =>
          ProductEditionAllowedLayersMap[productEdition] &&
          !ProductEditionAllowedLayersMap[productEdition].includes(l.type),
      )
    ) {
      dispatch(setAllowLayerEditing(false));
    }

    const [allPlateScores] = await Promise.all([
      allPlateScoresPromise,
      loadIngredientsPromise,
    ]);

    if (documentType === ETreeNodeTypes.Experiment) {
      expData.plateDto = expData.plateDto.map((plate) => {
        const plateId = plate.plateId;
        if (allPlateScores.allScores[plateId]) {
          return {
            ...plate,
            plateScores: getPlainPlateScores(allPlateScores.allScores[plateId]),
          } as Plate;
        }
        return plate;
      });
    }
    expData.isDirty = false;
    expData.deletedPlateIds = [];
    let experimentData = generateEmptyCanvasItems(expData);
    experimentData.plateDto.sort((x, y) => x.plateNumber - y.plateNumber);
    experimentData.plateLables = createPlateLables(
      experimentData.plateDto,
      experimentData.colCount,
    );
    experimentData = getPlainObject(experimentData);
    dispatch(setLayersInfo(layers));
    dispatch(updateExperimentDataState(experimentData));
    dispatch(setPlateSize(getExperimentPlateSize(experimentData)));

    // perf: preserve exp data only for editable (non-dispensed/imaged) experiment or info path at fetch
    const anyPlateDispensedOrImaged = experimentData?.plateDto?.some(
      (x) => x.state >= EPlateState.Dispensed,
    );
    if (!anyPlateDispensedOrImaged || isExpInfoPath()) {
      dispatch(preserveExpData());
    }

    const wellSelection = getDefaultWellSelection(expData);
    if (wellNumber && plateId) {
      wellSelection.wellNavigationDetails[0] = {
        ...wellSelection.wellNavigationDetails[0],
        wellNum: wellNumber,
        plateID: plateId,
        plateNum: expData.plateDto.find((p) => p.plateId === plateId)?.plateNumber,
      };
    }
    if (!getState().experiment?.wellSelectionDetails?.wellNavigationDetails?.length) {
      dispatch(setWellSelection(wellSelection));
    }
    recentDocumentService.saveDocumentToRecents(tempExp.treeNodeId);

    //Fetch metadata for remaining inspections
    if (expData.inspections?.length > 0) {
      expData.inspections
        .filter((i) => i.id !== inspectionSet?.id)
        .forEach((inspection) => {
          dispatch(
            fetchWellDropImageMetaDataThunk({
              experimentId: expId,
              inspectionId: inspection.id,
              batchIds: inspection.groupedInspections?.map((gi) => gi.imageBatchId),
            }),
          );
        });
    }
    dispatch(setIsFetchingExperimentData(false));
    return getState()?.experiment?.expData;
  };
};

export const fetchDrawingIdentifiersThunk = createAsyncThunk<
  PlateDrawingIdentifiers[],
  { plates: Plate[]; wellDetails: WellDetailsStates }
>('fetchDrawingIdentifiersThunk', async (params, thunkApi) => {
  try {
    const { plates, wellDetails } = params;
    const plateIDs = plates.map((plate) => plate.plateId);
    const reqData: PlateIngredientAndDrawingRequestDTO =
      new PlateIngredientAndDrawingRequestDTO();
    reqData.plateIDs = plateIDs;
    reqData.regionName = wellDetails.regionName;
    reqData.cpID = wellDetails.cpID;
    const drawingIdentifiers = await plateService.getPlatesDrawingIdentifiers(reqData);
    return drawingIdentifiers;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const fetchAllCompositeImagesThunk = createAsyncThunk<
  PlateCompositeImage[],
  {
    plates: PlateImageStructure[];
    wellNavigationDetails: WellNavigationDetails;
    wellImagingSettingDetails: DropImageParameters;
    regenerate: boolean;
  }
>('fetchAllCompositeImagesThunk', async (params, thunkApi) => {
  try {
    const { plates, wellNavigationDetails, wellImagingSettingDetails, regenerate } =
      params;
    const existingCompositeImages: PlateCompositeImage[] = plates.reduce((acc, plate) => {
      // RFR: In case new images generated, flush this dictionary
      const plateInspection =
        wellImagingSettingDetails.inspection.groupedInspections.find(
          (gi) => gi.plateId === plate.plateId,
        );
      if (!plateInspection) {
        return acc;
      }
      const compositeImageKey = getCompositeImageKeysAsString(
        { ...wellNavigationDetails, plateID: plate.plateId },
        { ...wellImagingSettingDetails, inspection: plateInspection },
      );
      const compositeImage = getCompositeImageStoreImageByKey(compositeImageKey);
      if (compositeImage || compositeImage == 'NoImage') {
        acc.push({
          plateId: plate.plateId,
          image: compositeImage,
          compositeImageKey: compositeImageKey,
        } as PlateCompositeImage);
      }

      return acc;
    }, [] as PlateCompositeImage[]);

    if (existingCompositeImages?.length && !regenerate) {
      return existingCompositeImages;
    }

    if (wellImagingSettingDetails?.inspection) {
      const updatedPlates = await plateService.fetchAllCompositeImages(
        plates,
        wellImagingSettingDetails.dropNumber,
        wellImagingSettingDetails.regionName,
        wellImagingSettingDetails.cpId,
        wellImagingSettingDetails.inspection,
        regenerate,
      );
      return updatedPlates;
    }
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});
